import React, {useState, useEffect, useCallback, useRef, useMemo} from "react";
import {AgGridReact} from "ag-grid-react";
import {
    ClientSideRowModelModule, LicenseManager, ModuleRegistry, RowGroupingModule,
} from "ag-grid-enterprise";
import {
    Button,
    Box,
    Typography,
    Menu,
    MenuItem,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    TextField,
    FormControl,
    InputLabel,
    Select, IconButton,
} from "@material-ui/core";
import Breadcrumbs from "@material-ui/core/Breadcrumbs";
import {Link as ReactLink, useNavigate, useParams} from "react-router-dom";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import CheckCircleOutlineIcon from "@material-ui/icons/CheckCircleOutline";
import AddCircleOutlineIcon from "@material-ui/icons/AddCircleOutline";
import {toast} from "react-toastify";
import "../../App.css";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import "react-toastify/dist/ReactToastify.css";
import fnOpenToast from "../../components/toast";
import RedoIcon from "@material-ui/icons/Redo";
import {
    createFooterNote,
    getFooterNotes,
    rearrangeFooterNotes,
    updateFooterNotes,
    getEnums
} from "../../api";
import MultiSelectCheckbox from "../../components/multiSelectCheckbox";
import EditIcon from "@material-ui/icons/Edit";
import withMetadataIdsMultiselectCheckbox from "../../hocs/withMetadataIdsGridMultiselectCheckbox";
import {notifyError} from "../../utils/toast";
import CreateFooterNoteDialog from "../../components/createFooterNoteDialog";
import ArrowUpwardIcon from "@material-ui/icons/ArrowUpward";
import ArrowDownwardIcon from "@material-ui/icons/ArrowDownward";
import DragIndicatorIcon from "@material-ui/icons/DragIndicator";

LicenseManager.setLicenseKey("Using_this_{AG_Grid}_Enterprise_key_{AG-062453}_in_excess_of_the_licence_granted_is_not_permitted___Please_report_misuse_to_legal@ag-grid.com___For_help_with_changing_this_key_please_contact_info@ag-grid.com___{Lucid_USA,_Inc}_is_granted_a_{Single_Application}_Developer_License_for_the_application_{V360}_only_for_{1}_Front-End_JavaScript_developer___All_Front-End_JavaScript_developers_working_on_{V360}_need_to_be_licensed___{V360}_has_been_granted_a_Deployment_License_Add-on_for_{2}_Production_Environments___This_key_works_with_{AG_Grid}_Enterprise_versions_released_before_{28_August_2025}____[v3]_[01]_MTc1NjMzNTYwMDAwMA==83cd4edac4587e17c3187cf63f89d914",);
ModuleRegistry.registerModules([ClientSideRowModelModule, RowGroupingModule]);

toast.configure();

const initialData = {
    text: {value: null, hasError: false},
    frText: {value: null, hasError: false},
    dateMatchField: {value: null, hasError: false},
    documentType: {value: null, hasError: false},
    startDate: {value: null, hasError: false},
    endDate: {value: null, hasError: false},
    titleStatus: {value: null, hasError: false},
    vehicleModel: {value: null, hasError: false},
};

function DocumentFooterNotes() {
    const {country: currentCountry} = useParams();
    const gridRef = useRef();
    const [gridApi, setGridApi] = useState(null);
    const [footerNotes, setFooterNotes] = useState([]);
    const [anchorEl, setAnchorEl] = useState(null);
    const [formData, setFormData] = useState(initialData);
    const [open, setOpen] = useState(false);
    const [country, setCountry] = useState(currentCountry);
    const [constantsData, setConstantsData] = useState(null);
    const [enableEditing, setEnableEditing] = useState(false);
    const [rowsUpdated, setRowsUpdated] = useState(new Map());
    const [editingField, setEditingField] = useState("");
    const [openTextEditor, setOpenTextEditor] = useState(false);
    const [textEditorValue, setTextEditorValue] = useState("");
    const [editingRow, setEditingRow] = useState(null);
    const [isRearrangeEnabled, setIsRearrangeEnabled] = useState(false);

    const navigate = useNavigate();

    const buildMultipleSelectColumn = (headerName, collection) => ({
        headerName: headerName,
        field: collection,
        cellEditor: withMetadataIdsMultiselectCheckbox(MultiSelectCheckbox),
        cellEditorParams: (params) => {
            return {
                values: validConstantsData[collection] ?? [], eGridCell: params.eGridCell,
            };
        },
        width: 200,
        cellRenderer: ({data}) => {
            const collectionData = data[collection];
            if (!constantsData[collection]) {
                return null;
            }
            if (collectionData?.length === 0) {
                return "All";
            }
            return constantsData[collection]
                .filter((item) => collectionData?.map((el) => el)?.includes(item?._id))
                .map((item) => item?.name)
                .join(", ");
        },
        valueGetter: ({data}) => data[collection],
        valueSetter: ({data, newValue}) => {
            data[collection] = newValue;
            return true;
        },
    });

    const buildEnumSelectorColumn = (headerName, field, enumName) => ({

        headerName,
        field,
        cellEditor: "agRichSelectCellEditor",
        cellEditorPopup: true,
        filter: false,
        flex: 2,
        cellEditorParams: () => {
            const values = validConstantsData?.[enumName]?.map(item => item.name) ?? [];
            return {values};
        },
        cellRenderer: ({value}) => {
            return value || '';
        },
        valueGetter: (params) => {
            return params.data[field] || '';
        },
        valueSetter: ({data, newValue}) => {
            data[field] = newValue;
            return true;
        },
        valueFormatter: (params) => {
            return params.value || '';
        },
    });

    const validConstantsData = useMemo(() => {
        const countryConstants = {};
        if (!constantsData) return countryConstants;
        for (const [collection, data] of Object.entries(constantsData)) {
            countryConstants[collection] = data.filter(({countries, isActive}) => {
                if (!countries) return true;
                return countries.includes(country) && isActive;
            });
        }
        return countryConstants;
    }, [constantsData, country]);


    const columnDefs = [
        {
            headerName: "Text",
            field: "text",
            filter: false,
            flex: 2,
            editable: false,
            cellRenderer: (params) => {
                const text = params.value || "";
                const displayText = text.length > 100 ? text.substring(0, 100) + "..." : text;
                return (
                    <div style={{cursor: "pointer"}}
                         onClick={() => enableEditing && handleOpenTextEditor(params.data, "text")}>
                        {displayText}
                    </div>);
            },
        },
        {
            headerName: "French Translation",
            field: "frText",
            filter: false,
            flex: 2,
            editable: false,
            cellRenderer: (params) => {
                const text = params.value || "";
                const displayText = text.length > 100 ? text.substring(0, 100) + "..." : text;
                return (
                    <div style={{cursor: "pointer"}}
                         onClick={() => enableEditing && handleOpenTextEditor(params.data, "frText")}>
                        {displayText}
                    </div>);
            },
            hide: country === "US"
        },
        buildEnumSelectorColumn("Document Type", "documentType", "footerNotesDocumentTypes"),
        buildMultipleSelectColumn("Vehicle Model", "vehicleModel"),
        buildMultipleSelectColumn("Title Status", "titleStatus"),
        {
            headerName: "Start Date",
            field: "startDate",
            filter: false,
            flex: 1,
            cellEditor: "agTextCellEditor",
            cellEditorParams: {
                useFormatter: true,
            },
            valueParser: (params) => {
                const date = params.newValue;
                const isValidDate = !!date
                    ? /^\d{2}\/\d{2}\/\d{4}$/.test(date)
                    : true;
                if (!isValidDate) {
                    alert(
                        'Please enter a valid date in the format "MM/DD/YYYY".',
                    );
                    return params.oldValue;
                }
                return date;
            },
        },
        {
            headerName: "End Date",
            field: "endDate",
            filter: false,
            flex: 1,
            cellEditor: "agTextCellEditor",
            cellEditorParams: {
                useFormatter: true,
            },
            valueParser: (params) => {
                const date = params.newValue;
                const isValidDate = !!date
                    ? /^\d{2}\/\d{2}\/\d{4}$/.test(date)
                    : true;
                if (!isValidDate) {
                    alert(
                        'Please enter a valid date in the format "MM/DD/YYYY".',
                    );
                    return params.oldValue;
                }
                return date;
            },
        },
        buildEnumSelectorColumn("Date Match Field", "dateMatchField", "footerNotesMatchFields"),
        {
            headerName: "Active", field: "isActive", filter: false, flex: 1,
        },
        {
            headerName: "Rearrange",
            width: 120,
            cellRenderer: (params) => {
                if (isRearrangeEnabled) {
                    return (
                        <div>
                            <IconButton
                                size="small"
                                onClick={() => handleMoveRow(params.data._id, "up")}
                                disabled={params.rowIndex === 0}
                                style={{ padding: '2px' }}
                            >
                                <ArrowUpwardIcon fontSize="small"/>
                            </IconButton>
                            <IconButton
                                size="small"
                                onClick={() => handleMoveRow(params.data._id, "down")}
                                disabled={params.rowIndex === footerNotes.length - 1}
                                style={{ padding: '2px' }}
                            >
                                <ArrowDownwardIcon fontSize="small"/>
                            </IconButton>
                        </div>
                    );
                }
                return null;
            },
            sortable: false,
            filter: false,
            suppressMenu: true,
        }
    ];

    const refreshData = useCallback(async (selectedCountry) => {
        const enums = await getEnums();
        setConstantsData(enums);
        const data = await getFooterNotes(selectedCountry);
        setFooterNotes(data);
    }, []);

    const onGridReady = useCallback((params) => {
        setGridApi(params.api);
    }, []);

    useEffect(() => {
        refreshData(country);
    }, [refreshData, country]);

    const handleSave = async (e) => {
        try {
            if (e) e.preventDefault();
            const editedRows = Array.from(rowsUpdated.values());
            await handleUpdateFooterNotes(editedRows);
            setEnableEditing(false);
            setRowsUpdated(new Map());
            handleClose();
        } catch (e) {
            fnOpenToast("Error ", "error");
        }
    };

    const handleMoveRow = useCallback(
        (id, direction) => {
            setFooterNotes((prevNotes) => {
                const index = prevNotes.findIndex((row) => row._id === id);

                if (index === -1) return prevNotes;

                if ((direction === "up" && index === 0) ||
                    (direction === "down" && index === prevNotes.length - 1)) {
                    return prevNotes;
                }

                const newNotes = [...prevNotes];
                const [movedRow] = newNotes.splice(index, 1);

                if (direction === "up") {
                    newNotes.splice(index - 1, 0, movedRow);
                } else {
                    newNotes.splice(index + 1, 0, movedRow);
                }

                if (gridApi) {
                    setTimeout(() => {
                        const allRowNodes = [];
                        gridApi.forEachNode(node => allRowNodes.push(node));

                        const rowNode = allRowNodes.find(node =>
                            node.data && node.data._id === movedRow._id
                        );

                        if (rowNode) {
                            gridApi.flashCells({
                                rowNodes: [rowNode]
                            });

                            gridApi.ensureNodeVisible(rowNode, 'middle');
                        }
                    }, 50);
                }

                return newNotes;
            });
        },
        [gridApi]
    );

    const handleUpdateFooterNotes = async (editedRows) => {
        for (const row of editedRows) {
            const {startDate, endDate, dateMatchField} = row;
            if (!startDate && !endDate) {
                row.dateMatchField = null;
            } else if (!dateMatchField) {
                notifyError("Date Match Field is required when start date or end date are set");
                return;
            }

            row.startDate = row.startDate ? row.startDate : null;
            row.endDate = row.endDate ? row.endDate : null;

            const updateRequest = {
                updateFooterNotes: editedRows,
            };
            const confirm = window.confirm(
                "Are you sure, you want to update these rows ?",
            );
            if (!confirm) {
                return;
            }

            setEnableEditing(false);
            try {
                const errorOcurred = await updateFooterNotes(updateRequest);
                !errorOcurred
                    ? fnOpenToast("Fees Updated Successfully!", "success")
                    : notifyError(errorOcurred?.message || "Something went wrong");
            } catch (error) {
                notifyError(error.message);
            }

            await refreshData(currentCountry);
        }
    };

    const handleCellValueChanged = (event) => {
        if (event.column.colId !== "text") {
            const updatedData = {...event.data};
            setRowsUpdated((prev) => new Map(prev).set(event.data._id, updatedData));
        }
    };

    const handleSetCountry = async (event) => {
        const countrySelected = event.target.value;
        setCountry(countrySelected);
        setRowsUpdated(new Map());
        navigate(`/document-footer-notes/${countrySelected}`);
    };

    const handleEnableEdit = () => {
        setEnableEditing(true);
    };

    const handleActionClick = useCallback((event) => setAnchorEl(event.currentTarget), []);
    const handleActionClose = useCallback(() => setAnchorEl(null), []);
    const handleEnableRearrange = useCallback(() => {
        setIsRearrangeEnabled(true);
        setAnchorEl(null);
    }, []);

    const handleCancelChanges = useCallback(() => {
        refreshData(currentCountry);
        setIsRearrangeEnabled(false);
        setEnableEditing(false);
        setAnchorEl(null);
    }, [refreshData, currentCountry]);

    const handleAddFooterNote = useCallback(() => {
        setFormData(initialData);
        setOpen(true);
    }, []);

    const handleClose = () => {
        setOpen(false);
        setFormData(initialData);
    };

    const handleOpenTextEditor = (rowData, field) => {
        setEditingRow(rowData);
        setTextEditorValue(rowData[field] || "");
        setEditingField(field);
        setOpenTextEditor(true);
    };

    const handleSaveText = () => {
        const updatedFooterNotes = footerNotes.map((item) => {
            if (item._id === editingRow._id) {
                return {...item, [editingField]: textEditorValue};
            }
            return item;
        });
        setFooterNotes(updatedFooterNotes);
        setRowsUpdated((prev) =>
            new Map(prev).set(editingRow._id, {...editingRow, [editingField]: textEditorValue})
        );
        setOpenTextEditor(false);
    };

    const handleRearrange = async () => {
        const confirm = window.confirm(
            "Are you sure you want to save the changes? " +
            "This will affect the footer notes order in the corresponding document."
        );

        if (!confirm) {
            return;
        }

        try {
            await rearrangeFooterNotes({
                sortedIds: footerNotes.map(note => note._id),
                country
            });

            toast.success("Changes saved successfully!", {autoClose: 3000});
            setIsRearrangeEnabled(false);
            setAnchorEl(null);
        } catch (error) {
            toast.error("Error saving changes", {autoClose: 3000});
        }
    };

    const handleSaveAllChanges = useCallback((e) => {
        if (enableEditing) {
            handleSave(e);
        } else if (isRearrangeEnabled) {
            handleRearrange();
        }
    }, [enableEditing, isRearrangeEnabled, handleSave, handleRearrange]);

    const constructRequest = (formData) => {
        return {
            text: formData.text.value,
            frText: formData.frText.value ?? null,
            dateMatchField: formData.dateMatchField.value?.name,
            documentType: formData.documentType.value.name,
            startDate: formData.startDate.value ?? null,
            endDate: formData.endDate.value ?? null,
            titleStatus: formData.titleStatus.value,
            vehicleModel: formData.vehicleModel.value,
            country
        };
    };

    const handleFormSubmit = async (event) => {
        try {
            event.preventDefault();
            const request = constructRequest(formData);
            if ((request.startDate || request.endDate) && !request.dateMatchField) {
                notifyError("Date Match Field is required when start date or end date are set");
                return;
            }
            await createFooterNote(request);
            handleClose();
            refreshData(currentCountry);
            fnOpenToast(
                "Footer Note Saved Successfully!!!",
                "success",
            );
        } catch (error) {
            fnOpenToast(error?.message || "Oops Something Went Wrong!", "error");
        }
    };

    const onChange = (e) => {
        const {value, id, name} = e.target;
        setFormData({
            ...formData,
            [id ?? name]: {value: value, hasError: false},
        });
    };

    const onChecked = (e) => {
        const {checked, id} = e.target;
        setFormData({...formData, [id]: {value: checked, hasError: false}});
    };

    const defaultColDef = {
        sortable: true, resizable: true, editable: enableEditing,
    };

    return (<div>
        <Box margin="10px 20px">
            <Breadcrumbs aria-label="breadcrumb">
                <ReactLink color="inherit" to="/" className="breadcrumb-link">
                    Dashboard
                </ReactLink>
                <ReactLink
                    color="inherit"
                    to="/templates-management"
                    className="breadcrumb-link"
                >
                    Templates
                </ReactLink>
                <Typography color="textPrimary">Document Footer Notes</Typography>
            </Breadcrumbs>
        </Box>

        <Box display="flex" justifyContent="space-between" margin="10px 20px">
            <Typography
                variant="h5"
                component="h5"
                style={{
                    fontFamily: "GTAmericaMedium", fontSize: "24px", lineHeight: "120%", fontWeight: "500",
                }}
            >
                Document Footer Notes
            </Typography>

            <Box display="flex" justifyContent="space-between" alignItems="center">
                <div style={{marginRight: "10px"}}>
                    <FormControl variant="outlined">
                        <InputLabel id="demo-simple-select-autowidth-label">
                            Country
                        </InputLabel>
                        <Select
                            labelId="demo-simple-select-outlined-label"
                            id="demo-simple-select-outlined"
                            value={country}
                            onChange={handleSetCountry}
                            label="Country"
                        >
                            <MenuItem value={"US"}>US</MenuItem>
                            <MenuItem value={"CA"}>CA</MenuItem>
                        </Select>
                    </FormControl>
                </div>

                <Button variant="outlined" color="primary" onClick={handleActionClick}>
                    Actions <ExpandMoreIcon/>
                </Button>
                <Menu
                    id="action-menu"
                    anchorEl={anchorEl}
                    keepMounted
                    open={Boolean(anchorEl)}
                    onClose={handleActionClose}
                >
                    <MenuItem
                        onClick={handleEnableEdit}
                        disabled={isRearrangeEnabled}
                    >
                        <EditIcon fontSize="small" style={{marginRight: "5px"}}/> Enable Editing
                    </MenuItem>
                    <MenuItem
                        onClick={handleAddFooterNote}
                        disabled={enableEditing || isRearrangeEnabled}
                    >
                        <AddCircleOutlineIcon fontSize="small" style={{marginRight: "5px"}}/> Add Footer Note
                    </MenuItem>
                    <MenuItem
                        onClick={handleEnableRearrange}
                        disabled={enableEditing}
                    >
                        <DragIndicatorIcon
                            fontSize="small"
                            style={{marginRight: "5px"}}
                        />{" "}
                        Enable Rearrange
                    </MenuItem>
                    {(enableEditing || isRearrangeEnabled) && (
                        <MenuItem onClick={handleSaveAllChanges}>
                            <CheckCircleOutlineIcon fontSize="small" style={{marginRight: "5px"}}/>
                            Save Changes
                        </MenuItem>
                    )}
                    {(enableEditing || isRearrangeEnabled) && (
                        <MenuItem onClick={handleCancelChanges}>
                            <RedoIcon fontSize="small" style={{marginRight: "5px"}}/> Cancel Changes
                        </MenuItem>
                    )}
                </Menu>
            </Box>
        </Box>
        <div
            style={{
                margin: "0 20px", height: "calc(100vh - 170px)", border: "1px solid #babfc7",
            }}
        >
            <div className="ag-theme-alpine" style={{height: "100%", width: "100%"}}>
                <AgGridReact
                    columnDefs={columnDefs}
                    defaultColDef={defaultColDef}
                    rowData={footerNotes}
                    suppressAutoSize={true}
                    onGridReady={onGridReady}
                    ref={gridRef}
                    rowSelection="multiple"
                    onCellValueChanged={handleCellValueChanged}
                />
            </div>
        </div>

        {constantsData && (
            <CreateFooterNoteDialog
                open={open}
                handleClose={handleClose}
                data={formData}
                onChange={onChange}
                onChecked={onChecked}
                handleFormSubmit={handleFormSubmit}
                constantsData={validConstantsData}
                country={currentCountry}
            />
        )}

        <Dialog
            open={openTextEditor}
            onClose={() => setOpenTextEditor(false)}
            fullWidth
            maxWidth="sm"
        >
            <DialogTitle>
                {editingField === "text" ? "Edit Text" : "Edit French Translation"}
            </DialogTitle>
            <DialogContent>
                <TextField
                    autoFocus
                    value={textEditorValue}
                    onChange={(e) => setTextEditorValue(e.target.value)}
                    multiline
                    rows={4}
                    variant="outlined"
                    fullWidth
                    onKeyDown={(e) => e.stopPropagation()}
                    label={editingField === "text" ? "Text Content" : "French Translation"}
                />
            </DialogContent>
            <DialogActions>
                <Button onClick={() => setOpenTextEditor(false)} color="primary">
                    Cancel
                </Button>
                <Button onClick={handleSaveText} color="primary">
                    Save
                </Button>
            </DialogActions>
        </Dialog>
    </div>);
}

export default DocumentFooterNotes;
